"use client";

import React from "react";
import { Phone, Mail, MapPin, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
export default function RequestAQuote() {
  return <section data-section-id="1707" id="start-a-trade-conversation" className="bg-background py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <h2 className="font-sans text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight tracking-tight">
              Initiate Your Global Trade Partnership
            </h2>
            <p className="text-foreground/70 text-lg mb-12 max-w-md font-sans">
              Connect with Cosmic EXIM LLC to bridge the gap between premium Indian sourcing and global markets. Let's discuss your bulk requirements and logistics today.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-secondary/10 p-3 rounded-xl">
                  <Phone className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-sans text-lg font-bold text-foreground mb-1">Trade Desk</h3>
                  <p className="text-foreground/80">+1 (919) 234-5678</p>
                  <p className="text-sm text-foreground/60 mt-1">Mon-Fri, 9am - 6pm EST</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-secondary/10 p-3 rounded-xl">
                  <Mail className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-sans text-lg font-bold text-foreground mb-1">Email Inquiries</h3>
                  <p className="text-foreground/80">trade@cosmicexim.com</p>
                  <p className="text-sm text-foreground/60 mt-1">24/7 Global Support</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-secondary/10 p-3 rounded-xl">
                  <MapPin className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-sans text-lg font-bold text-foreground mb-1">Global Presence</h3>
                  <p className="text-foreground/80">
                    Apex, North Carolina, USA<br />
                    Hyderabad, Telangana, India
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 bg-card p-8 md:p-12 shadow-xl border border-border/50 rounded-[24px]">
            <div className="mb-8">
              <h3 className="font-sans text-2xl font-bold text-foreground">Trade Inquiry</h3>
              <p className="text-foreground/70 mt-2 font-sans">Tell us about your sourcing needs and our trade specialists will respond within 24 hours.</p>
            </div>

            <FormProvider formId="cosmic_trade_inquiry" sectionName="start-a-trade-conversation" defaultValues={{
            full_name: "",
            company_name: "",
            email: "",
            phone: "",
            product_type: "",
            quantity: "",
            additional_details: ""
          }} validationRules={{
            full_name: {
              required: "Full Name is required"
            },
            company_name: {
              required: "Company Name is required"
            },
            email: {
              required: "Email is required",
              pattern: "email"
            },
            phone: {
              required: "Phone Number is required",
              pattern: "phone"
            },
            product_type: {
              required: "Please select a trade category"
            }
          }} submitText="Send Inquiry" successMessage="Thank you. Your inquiry has been received. A trade specialist will contact you shortly.">
              <QuoteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const inputClasses = "bg-transparent border-0 border-b border-primary/20 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-secondary h-12 text-base font-sans placeholder:text-foreground/40 transition-colors";
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 bg-accent/20 rounded-[24px]">
        <div className="w-16 h-16 bg-secondary/10 flex items-center justify-center rounded-full mb-4">
          <ArrowRight className="w-8 h-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-foreground">Inquiry Received</h3>
        <p className="text-foreground/70 max-w-md font-sans">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold font-sans">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your name" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold font-sans">Company / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Cosmic EXIM LLC" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold font-sans">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="partner@example.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold font-sans">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (919) 000-0000" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="product_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold font-sans">Trade Interest</FormLabel>
              <FormControl>
                <select {...field} className={cn(inputClasses, "w-full appearance-none cursor-pointer")}>
                  <option value="" disabled>Select a category</option>
                  <option value="Bulk Rice">Bulk Rice Inquiry</option>
                  <option value="Spices & Mirchi">Spice & Mirchi Sourcing</option>
                  <option value="Cocoa & Coffee">Cocoa & Coffee Imports</option>
                  <option value="Eco-Products">Eco-Product Partnership</option>
                  <option value="Live Plants">Live Plant Logistics</option>
                  <option value="Other">Other Trade Inquiry</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="quantity" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold font-sans">Estimated Volume <span className="text-foreground/50 font-normal text-xs ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="text" placeholder="e.g., 20ft Container" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold font-sans">Sourcing Requirements <span className="text-foreground/50 font-normal text-xs ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Specify quality grades, delivery timelines, or custom packaging needs." className={cn(inputClasses, "h-32 py-3 resize-none")} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans rounded-lg">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full text-lg font-bold tracking-wider uppercase shadow-lg transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Processing Inquiry...
          </> : "Initiate Conversation"}
      </Button>
    </Form>;
}